import Axios from 'axios'
import FetcherHelper from '@/utils/fetcherHelper'
import Config from '@/config'

export default class TimeseriesProxy {
  static get baseUrl () {
    return `${Config.apiBaseUrl}/data`
  }

  static getTemplateValidatedDatasources (observationPointId) {
    // https://github.com/crealp/strates-db/wiki/Ajout-d'un-point-d'observation
    return [
      // HEAD_LEVEL
      {
        observationPointId,
        parameterTypeId: 16,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // TEMP
      {
        observationPointId,
        parameterTypeId: 4,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // EC
      {
        observationPointId,
        parameterTypeId: 5,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '1110'
      }
    ]
  }

  static getTemplateRemoteDatasources (observationPointId) {
    // https://github.com/crealp/strates-db/wiki/Ajout-d'un-point-d'observation
    return [
      // HEAD_PRESSURE
      {
        observationPointId,
        parameterTypeId: 8,
        dataSourceTypeId: 1,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // TEMP
      {
        observationPointId,
        parameterTypeId: 4,
        dataSourceTypeId: 1,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // EC
      {
        observationPointId,
        parameterTypeId: 5,
        dataSourceTypeId: 1,
        rawPeriod: null,
        canAggregate: '1110'
      }
    ]
  }

  static getTemplateReferenceDatasources (observationPointId) {
    // https://github.com/crealp/strates-db/wiki/Ajout-d'un-point-d'observation
    return [
      // HEIGHT_BSL
      {
        observationPointId,
        parameterTypeId: 10,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '0000'
      },
      // HEIGHT_SFL
      {
        observationPointId,
        parameterTypeId: 11,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '0000'
      },
      // PROBE_HEIGHT_BSL
      {
        observationPointId,
        parameterTypeId: 9,
        dataSourceTypeId: 2,
        rawPeriod: null,
        canAggregate: '0000'
      }
    ]
  }

  static getTemplateForecastDatasources (observationPointId) {
    // https://github.com/crealp/strates-db/wiki/Ajout-d'un-point-d'observation
    return [
      // QINT
      {
        observationPointId,
        parameterTypeId: 7,
        dataSourceTypeId: 3,
        rawPeriod: null,
        canAggregate: '0000'
      },
      // VAP
      {
        observationPointId,
        parameterTypeId: 6,
        dataSourceTypeId: 3,
        rawPeriod: null,
        canAggregate: '0000'
      },
      // HEADS_FORECAST_7
      {
        observationPointId,
        parameterTypeId: 12,
        dataSourceTypeId: 4,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // HEADS_FORECAST_14
      {
        observationPointId,
        parameterTypeId: 13,
        dataSourceTypeId: 4,
        rawPeriod: null,
        canAggregate: '1110'
      },
      // HEADS_FORECAST_21
      {
        observationPointId,
        parameterTypeId: 14,
        dataSourceTypeId: 4,
        rawPeriod: null,
        canAggregate: '1110'
      }
    ]
  }

  static getDatasource(datasourceId) {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/data-source/${datasourceId}`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static extract ({
    datasourceId = null,
    dateFrom = null,
    dateTo = null,
    aggregationMode = 'raw',
    stats = null
  } = {}) {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/${datasourceId}/${aggregationMode}`, {
        params: {
          dateFrom,
          dateTo,
          stats
        },
        ...FetcherHelper.getHeaders()
      })
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static export ({
    datasourceId = null,
    dateFrom = null,
    dateTo = null,
    aggregationMode = 'raw',
    stats = null
  } = {}) {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/export/${datasourceId}/${aggregationMode}`, {
        params: {
          dateFrom,
          dateTo,
          stats
        },
        ...FetcherHelper.getHeaders()
      })
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static fetchTimeseries (datasources, dateFrom, dateTo, aggregationMode) {
    return new Promise((resolve, reject) => {
      let toFetch = []
      datasources.forEach((datasource) => toFetch.push(TimeseriesProxy.extract({ datasourceId: datasource.id, dateFrom, dateTo, aggregationMode })))

      Promise.all(toFetch).then((timeseries) => {
        let toReturn = []
        timeseries.forEach((data, index) => {
          toReturn.push({
            datasource: datasources[index],
            data
          })
        })
        resolve(toReturn)
      }).catch(reject)
    })
  }

  static getPastEvolution (datasourceId) {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/${datasourceId}/past-evolution`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static getLastValue (datasourceId, includeNoData = true) {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/${datasourceId}/last-value?includeNoData=${includeNoData}`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static addValue(datasourceId, value, aggregationMode = 'raw') {
    return new Promise((resolve, reject) => {
      Axios.post(`${TimeseriesProxy.baseUrl}/${datasourceId}/${aggregationMode}`, value, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static updateValue(datasourceId, event, value) {
    return new Promise((resolve, reject) => {
      Axios.put(`${TimeseriesProxy.baseUrl}/${datasourceId}/${event}`, value, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }


  static deleteValue(datasourceId, event) {
    return new Promise((resolve, reject) => {
      Axios.delete(`${TimeseriesProxy.baseUrl}/${datasourceId}/${event}`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static importValues({
    datasourceId = 0,
    aggregationMode = 'raw',
    data = [],
    dryRun = true,
    force = false,
    updateIfExists = false
  } = {}) {
    return new Promise((resolve, reject) => {
      Axios.post(`${TimeseriesProxy.baseUrl}/${datasourceId}/${aggregationMode}/import`, {
        data,
        dryRun,
        force,
        updateIfExists
      }, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static listParameterTypes () {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/data-source/parameter-type`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static listDatasourceTypes() {
    return new Promise((resolve, reject) => {
      Axios.get(`${TimeseriesProxy.baseUrl}/data-source/type`, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }

  static createDatasource (observationPointId, parameterTypeId, datasourceTypeId, rawPeriod = null, canAggregate = '1110') {
    return new Promise((resolve, reject) => {
      Axios.post(`${TimeseriesProxy.baseUrl}/data-source`, {
        observationPointId,
        parameterTypeId,
        dataSourceTypeId: datasourceTypeId,
        rawPeriod,
        canAggregate
      }, FetcherHelper.getHeaders())
        .then((response) => {
          resolve(response.data)
        }).catch(reject)
    })
  }
}